import { useCallback, useEffect, useRef, useState } from 'react'

import { isEmpty } from 'lodash'

// Rótulos de modo de operação (espelha o enum da API: assisted/automated/...).
const OPERATION_MODE_LABELS = {
  assisted: 'Assistida',
  automated: 'Automatizada',
  autonomous: 'Autônoma',
  hybrid: 'Híbrida',
}

const RISK_LABELS = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
}

// Converte um item de inventário da API (aiInventoryItems) no formato esperado
// pelo painel lateral (InventoryItemCard). A API entrega só os ids das
// categorias de risco (riskOptionIds); os rótulos vêm do catálogo
// (riskOptionsById: { [id]: label }), resolvido por quem chama.
export const mapAiInventoryToPanelItem = (inventory, riskOptionsById = {}) => {
  const tool = inventory.aiInventoryTool || {}
  const provider = tool.aiInventoryProvider || {}
  const purposes = inventory.aiInventoryPurposes || inventory.purposes || []
  const riskOptionIds = inventory.riskOptionIds || []
  const departments = inventory.departments || []

  const humanIntervention =
    inventory.humanIntervention === true
      ? 'Sim'
      : inventory.humanIntervention === false
      ? 'Não'
      : ''

  const processesPersonalData =
    inventory.processesPersonalData === true
      ? 'Sim'
      : inventory.processesPersonalData === false
      ? 'Não'
      : ''

  return {
    id: inventory.id,
    name: inventory.name || tool.name || 'IA',
    subtitle: inventory.description || provider.name || '',
    iconUrl: tool.iconUrl || provider.iconUrl || null,
    details: {
      // Um departamento por linha (white-space: pre-line no fieldValue).
      departments: inventory.allDepartments
        ? 'Todos os departamentos'
        : departments
            .map((department) => department.name)
            .filter(Boolean)
            .join('\n'),
      // Uma finalidade por linha (renderizado com white-space: pre-line).
      purpose: purposes
        .map((purpose) => purpose.description || purpose.name)
        .filter(Boolean)
        .join('\n'),
      features: inventory.description || '',
      operationMode:
        OPERATION_MODE_LABELS[inventory.operationMode] ||
        inventory.operationMode ||
        '',
      humanIntervention,
      processesPersonalData,
      risk: RISK_LABELS[inventory.risk] || '',
      // Uma categoria de risco por linha (white-space: pre-line no fieldValue).
      riskOptions: riskOptionIds
        .map((id) => riskOptionsById[id])
        .filter(Boolean)
        .join('\n'),
    },
  }
}

// Extrai apenas o texto autoral do agente; ignora partes de functionCall /
// functionResponse (atividade de tools).
const extractTextChunk = (parts) =>
  parts
    .filter((part) => part.text)
    .map((part) => part.text)
    .join('')

const ITEM_ID_ARG_KEY = 'ai_inventory_item_id'

// Descobre, nos eventos SSE de um turno, os ids dos itens que o agente tocou —
// editar (update/purpose/risk → id no functionCall.args) ou criar (id no
// functionResponse). É o sinal de quais IAs estão sendo mexidas na conversa,
// mais confiável que updatedAt (adicionar finalidade não muda o updated_at).
// Retorna TODOS os ids do evento, na ordem encontrada: num único turno o agente
// pode cadastrar/mexer em mais de uma IA (ex.: "cadastre ChatGPT e Gemini").
const extractTouchedItemIds = (parts) => {
  const touchedIds = []
  parts.forEach((part) => {
    const argId = part.functionCall?.args?.[ITEM_ID_ARG_KEY]
    if (argId != null) touchedIds.push(argId)

    const createdId =
      part.functionResponse?.response?.output?.ai_inventory_item?.id
    if (createdId != null) touchedIds.push(createdId)
  })
  return touchedIds
}

/**
 * Gerencia uma sessão de chat conversacional com a DAI (inventário de IA) e o
 * inventário cadastrado, consumindo a stream SSE do ADK.
 *
 * @param {object}   params
 * @param {object}   params.chatClient   - cliente de chat (service.dai.chat | guestChat)
 * @param {string}   params.appName      - app do agente (ai_inventory_agent | ai_inventory_guest_agent)
 * @param {string}   params.userId       - id da identidade atual (usuário ou convidado)
 * @param {object}   [params.sessionState] - estado inicial da sessão (ex.: { departmentId })
 * @param {Function} [params.loadItems]  - retorna a lista atual de itens já mapeados para o painel
 * @param {Function} [params.loadItem]   - busca um único item (por id) mapeado para o painel, para refresh incremental
 */
const useAiInventoryDaiChat = ({
  chatClient,
  appName,
  userId,
  sessionState,
  loadItems,
  loadItem,
  welcomeMessage,
}) => {
  // Sequência local por instância do hook — evita colisão de ids (e de chaves
  // do React) quando há mais de uma instância montada.
  const messageIdSeqRef = useRef(0)
  const nextMessageId = useCallback(() => {
    messageIdSeqRef.current += 1
    return messageIdSeqRef.current
  }, [])

  const [messages, setMessages] = useState(() =>
    welcomeMessage
      ? [{ id: nextMessageId(), role: 'assistant', text: welcomeMessage }]
      : [],
  )
  const [items, setItems] = useState([])
  const [isSending, setIsSending] = useState(false)
  // Id do item que o agente tocou por último — a IA "ativa" na conversa.
  const [activeItemId, setActiveItemId] = useState(null)

  const sessionIdRef = useRef(null)
  const abortControllerRef = useRef(null)
  const isSendingRef = useRef(false)
  // Ids do inventário que já existiam quando a conversa abriu. O painel mostra
  // só o que foi cadastrado NESTA sessão (fluxo de cadastro de coisas novas).
  const baselineIdsRef = useRef(null)

  const refreshItems = useCallback(async () => {
    if (!loadItems) return
    try {
      const fetchedItems = (await loadItems()) || []

      if (baselineIdsRef.current === null) {
        baselineIdsRef.current = new Set(fetchedItems.map((item) => item.id))
      }

      setItems(
        fetchedItems.filter((item) => !baselineIdsRef.current.has(item.id)),
      )
    } catch {
      // Painel apenas não atualiza; não interrompe a conversa.
    }
  }, [loadItems])

  // Insere/atualiza um único item no painel (itens do baseline não aparecem).
  const mergeItem = useCallback((panelItem) => {
    if (!panelItem) return
    if (baselineIdsRef.current?.has(panelItem.id)) return

    setItems((prev) => {
      const exists = prev.some((item) => item.id === panelItem.id)
      return exists
        ? prev.map((item) => (item.id === panelItem.id ? panelItem : item))
        : [...prev, panelItem]
    })
  }, [])

  // Atualização incremental: busca só os itens tocados no turno, evitando
  // refazer a listagem detalhada inteira a cada mensagem. Sem ids (ou sem loader
  // incremental, ou em falha) cai para o refresh completo. Cada item é buscado
  // e mesclado de forma independente — uma falha pontual não derruba os demais.
  const refreshTouchedItems = useCallback(
    async (itemIds) => {
      if (isEmpty(itemIds) || !loadItem) return refreshItems()
      await Promise.all(
        itemIds.map(async (itemId) => {
          try {
            mergeItem(await loadItem(itemId))
          } catch {
            await refreshItems()
          }
        }),
      )
    },
    [loadItem, mergeItem, refreshItems],
  )

  const ensureSession = useCallback(async () => {
    if (sessionIdRef.current) return sessionIdRef.current

    const response = await chatClient.createSession({
      appName,
      userId,
      ...(sessionState && Object.keys(sessionState).length > 0
        ? { state: sessionState }
        : {}),
    })

    sessionIdRef.current = response.data?.id || response.data?.sessionId
    return sessionIdRef.current
  }, [appName, chatClient, sessionState, userId])

  // Ao montar: carrega o inventário atual e pré-cria a sessão (injeta o estado
  // do departamento). Ao desmontar: aborta qualquer stream em andamento.
  useEffect(() => {
    refreshItems()
    ensureSession().catch(() => {})

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const abortMessage = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
  }, [])

  const sendMessage = useCallback(
    async (text) => {
      const content = (text || '').trim()
      if (!content || isSendingRef.current) return

      if (abortControllerRef.current) abortControllerRef.current.abort()
      abortControllerRef.current = new AbortController()
      const { signal } = abortControllerRef.current

      isSendingRef.current = true
      setIsSending(true)

      const assistantId = nextMessageId()
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: 'user', text: content },
        { id: assistantId, role: 'assistant', text: '' },
      ])

      // Ids dos itens tocados neste turno — usados para o refresh incremental.
      // Set para não buscar o mesmo item várias vezes (o agente costuma tocar a
      // mesma IA em vários passos: criar, finalidade, operação, risco).
      const turnTouchedIds = new Set()
      let accumulatedText = ''
      const updateAssistant = (value) =>
        setMessages((prev) =>
          prev.map((message) =>
            message.id === assistantId ? { ...message, text: value } : message,
          ),
        )

      const errorMessage =
        'Desculpe, ocorreu um erro ao falar com a DAI. Tente novamente.'

      try {
        const sessionId = await ensureSession()

        // userId NÃO vai no corpo das rotas de runtime: o adaptador user-scoped
        // o injeta a partir do token e rejeita (400) se vier informado.
        await chatClient.runSSE(
          {
            appName,
            sessionId,
            newMessage: { role: 'user', parts: [{ text: content }] },
            streaming: true,
          },
          {
            onMessage: (event) => {
              const parts = event?.content?.parts ?? []

              const touchedIds = extractTouchedItemIds(parts)
              if (!isEmpty(touchedIds)) {
                touchedIds.forEach((id) => turnTouchedIds.add(id))
                // IA "ativa" = a última tocada (foco do painel).
                setActiveItemId(touchedIds[touchedIds.length - 1])
              }

              const chunk = extractTextChunk(parts)
              if (!chunk) return

              // partial === false sinaliza o texto agregado final do turno.
              if (event.partial === false) accumulatedText = chunk
              else accumulatedText += chunk

              updateAssistant(accumulatedText)
            },
            onError: () => updateAssistant(accumulatedText || errorMessage),
            onAbort: () =>
              updateAssistant(accumulatedText || 'Geração interrompida.'),
            onDone: () => refreshTouchedItems([...turnTouchedIds]),
            signal,
          },
        )
      } catch {
        updateAssistant(accumulatedText || errorMessage)
      } finally {
        isSendingRef.current = false
        setIsSending(false)
      }
    },
    [appName, chatClient, ensureSession, refreshTouchedItems, userId],
  )

  return {
    messages,
    items,
    isSending,
    sendMessage,
    abortMessage,
    activeItemId,
  }
}

export default useAiInventoryDaiChat
